/**
 * ProductSearchAutocomplete Component
 *
 * Autocomplete for searching products in a supplier's catalog.
 * Shows product name, packaging options, and current price.
 * @see US-8.15 - Formulaire Creation Commande
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Search, Loader2, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks';
import { useCatalogItems } from '@/features/suppliers/hooks';
import type { SupplierCatalogItemWithDetails } from '@bstock/shared';

export interface ProductSearchAutocompleteProps {
  /** Supplier ID to search in */
  supplierId: string | null;
  /** Placeholder text */
  placeholder?: string;
  /** Callback when a catalog item is selected — includes all packaging variants for this product */
  onSelect: (item: SupplierCatalogItemWithDetails, variants: SupplierCatalogItemWithDetails[]) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
}

export function ProductSearchAutocomplete({
  supplierId,
  placeholder = 'Rechercher un produit...',
  onSelect,
  disabled = false,
  className,
}: ProductSearchAutocompleteProps) {
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebounce(searchValue, 300);

  // Fetch catalog items for the supplier
  const { data: catalogData, isLoading } = useCatalogItems(supplierId, {
    search: debouncedSearch || undefined,
    isActive: true,
    limit: 20,
  });

  const allCatalogItems = catalogData?.data ?? [];

  // Deduplicate by rawProductId — show one entry per product
  const options = useMemo(() => {
    const seen = new Map<string, SupplierCatalogItemWithDetails>();
    for (const item of allCatalogItems) {
      if (!seen.has(item.rawProductId)) {
        seen.set(item.rawProductId, item);
      }
    }
    return Array.from(seen.values());
  }, [allCatalogItems]);

  // Count packaging options per product
  const packagingCountByProduct = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of allCatalogItems) {
      counts[item.rawProductId] = (counts[item.rawProductId] || 0) + 1;
    }
    return counts;
  }, [allCatalogItems]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [options]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setIsOpen(true);
  }, []);

  const handleInputFocus = useCallback(() => {
    if (supplierId) {
      setIsOpen(true);
    }
  }, [supplierId]);

  const handleOptionSelect = useCallback(
    (item: SupplierCatalogItemWithDetails) => {
      const variants = allCatalogItems.filter((ci) => ci.rawProductId === item.rawProductId);
      onSelect(item, variants);
      setSearchValue('');
      setIsOpen(false);
      // Keep focus on input for quick successive additions
      inputRef.current?.focus();
    },
    [onSelect, allCatalogItems]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
          setIsOpen(true);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < options.length) {
            handleOptionSelect(options[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          break;
      }
    },
    [isOpen, options, highlightedIndex, handleOptionSelect]
  );

  const showDropdown = isOpen && !!supplierId && (searchValue.length >= 1 || options.length > 0);

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={supplierId ? placeholder : 'Selectionnez d\'abord un fournisseur'}
          disabled={disabled || !supplierId}
          className={cn(
            'flex h-10 w-full rounded-lg border bg-white pl-10 pr-3 py-2 text-sm transition-colors',
            'placeholder:text-neutral-400',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500',
            'disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400',
            'border-neutral-300 hover:border-neutral-400'
          )}
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          role="combobox"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 animate-spin" />
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-neutral-200 shadow-lg max-h-80 overflow-auto"
          role="listbox"
        >
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-neutral-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Recherche en cours...
            </div>
          ) : options.length === 0 ? (
            <div className="px-4 py-3 text-sm text-neutral-500">
              {searchValue.length < 1
                ? 'Commencez a taper pour rechercher'
                : 'Aucun produit trouve'}
            </div>
          ) : (
            options.map((item, index) => {
              const pkgCount = packagingCountByProduct[item.rawProductId] || 1;
              return (
                <button
                  key={item.rawProductId}
                  type="button"
                  className={cn(
                    'w-full px-4 py-3 text-left text-sm hover:bg-neutral-50 transition-colors',
                    'focus:outline-none focus:bg-neutral-50',
                    'border-b border-neutral-100 last:border-0',
                    highlightedIndex === index && 'bg-neutral-50'
                  )}
                  onClick={() => handleOptionSelect(item)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  role="option"
                  aria-selected={highlightedIndex === index}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-dark-500 truncate">
                        {item.rawProduct.name}
                      </div>
                      {item.rawProduct.sku && (
                        <div className="text-xs text-neutral-400 mt-0.5">
                          SKU: {item.rawProduct.sku}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-neutral-500">
                      <Package className="w-3 h-3" />
                      <span>{pkgCount} cond.</span>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
